import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import { API } from '../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

// Skeleton loader for property cards
const PropertySkeleton = () => (
  <div className="bg-white rounded-lg shadow-sm overflow-hidden animate-pulse">
    <div className="aspect-video bg-gray-200" />
    <div className="p-4 space-y-3">
      <div className="h-4 bg-gray-200 rounded w-3/4" />
      <div className="h-6 bg-gray-200 rounded w-1/2" />
      <div className="flex gap-2">
        <div className="h-4 bg-gray-200 rounded w-16" />
        <div className="h-4 bg-gray-200 rounded w-16" />
      </div>
      <div className="h-4 bg-gray-200 rounded w-full" />
    </div>
  </div>
);

// Lazy loaded image component
const LazyImage = ({ src, alt, className }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  return (
    <div className={`relative ${className}`}>
      {!loaded && !error && (
        <div className="absolute inset-0 bg-gray-200 animate-pulse flex items-center justify-center">
          <i className="fas fa-image text-gray-400 text-2xl"></i>
        </div>
      )}
      {error ? (
        <div className="absolute inset-0 bg-gray-100 flex items-center justify-center">
          <i className="fas fa-home text-gray-400 text-3xl"></i>
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
          className={`w-full h-full object-cover transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        />
      )}
    </div>
  );
};

const SuburbLandingPage = () => {
  const { suburb } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Extract listing type from the URL path
  const listingType = location.pathname.split('/')[1];
  
  const [pageData, setPageData] = useState(null);
  const [faqData, setFaqData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('relevance');
  const [showFAQ, setShowFAQ] = useState(true);

  // Format price
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      maximumFractionDigits: 0
    }).format(price);
  };

  // Fetch page data
  const fetchPageData = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch both page data and FAQ data in parallel
      const [pageRes, faqRes] = await Promise.all([
        axios.get(`${API}/seo/suburb-page/${listingType}/${suburb}`, {
          params: { page, limit: 20 }
        }),
        axios.get(`${API}/seo/location-faq/${listingType}/${suburb}`).catch(() => null)
      ]);
      
      setPageData(pageRes.data);
      if (faqRes) {
        setFaqData(faqRes.data);
      }
      setCurrentPage(page);
    } catch (err) {
      console.error('Error fetching suburb page:', err);
      if (err.response?.status === 404) {
        setError('Page not found. The suburb or listing type may be invalid.');
      } else {
        setError('Failed to load properties. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [listingType, suburb]);

  useEffect(() => {
    fetchPageData(1);
  }, [fetchPageData]);

  // Generate Schema.org structured data (ItemList + FAQPage)
  const generateSchemaData = () => {
    if (!pageData) return null;
    
    const itemListSchema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": pageData.seo.h1,
      "description": pageData.seo.description,
      "numberOfItems": pageData.pagination.total,
      "itemListElement": pageData.listings.map((listing, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "RealEstateListing",
          "name": listing.title,
          "url": `${window.location.origin}/listing/${listing.id}`,
          "offers": {
            "@type": "Offer",
            "price": listing.price,
            "priceCurrency": "ZAR"
          }
        }
      }))
    };
    
    // Include FAQ schema if available
    const schemas = [itemListSchema];
    if (faqData?.faqSchema) {
      schemas.push(faqData.faqSchema);
    }
    
    return JSON.stringify(schemas);
  };

  // Handle page navigation
  const handlePageChange = (newPage) => {
    fetchPageData(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading && !pageData) {
    return (
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Skeleton header */}
        <div className="mb-8 animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-64 mb-4" />
          <div className="h-10 bg-gray-200 rounded w-96 mb-2" />
          <div className="h-6 bg-gray-200 rounded w-80" />
        </div>
        
        {/* Skeleton grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <PropertySkeleton key={i} />
          ))}
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="max-w-7xl mx-auto px-4 py-12 text-center">
        <div className="w-20 h-20 mx-auto mb-6 bg-red-50 rounded-full flex items-center justify-center">
          <i className="fas fa-exclamation-triangle text-3xl text-red-400"></i>
        </div>
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Page Not Found</h1>
        <p className="text-gray-600 mb-6">{error}</p>
        <Button onClick={() => navigate('/marketplace')}>
          Browse All Properties
        </Button>
      </main>
    );
  }

  if (!pageData) return null;

  return (
    <>
      {/* SEO Head Tags */}
      <Helmet>
        <title>{pageData.seo.title}</title>
        <meta name="description" content={pageData.seo.description} />
        <link rel="canonical" href={`${window.location.origin}${pageData.seo.canonical}`} />
        
        {/* Open Graph Tags */}
        <meta property="og:title" content={pageData.seo.title} />
        <meta property="og:description" content={pageData.seo.description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${window.location.origin}${pageData.seo.canonical}`} />
        {pageData.seo.ogImage && <meta property="og:image" content={pageData.seo.ogImage} />}
        <meta property="og:site_name" content="Flippy Homes" />
        <meta property="og:locale" content="en_ZA" />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageData.seo.title} />
        <meta name="twitter:description" content={pageData.seo.description} />
        {pageData.seo.ogImage && <meta name="twitter:image" content={pageData.seo.ogImage} />}
        
        {/* Schema.org Structured Data */}
        <script type="application/ld+json">
          {generateSchemaData()}
        </script>
      </Helmet>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6" aria-label="Breadcrumb">
          {pageData.breadcrumbs.map((crumb, index) => (
            <React.Fragment key={crumb.url}>
              {index > 0 && <i className="fas fa-chevron-right text-xs text-gray-400"></i>}
              {index === pageData.breadcrumbs.length - 1 ? (
                <span className="text-gray-700 font-medium">{crumb.text}</span>
              ) : (
                <Link to={crumb.url} className="hover:text-emerald-600 transition-colors">
                  {crumb.text}
                </Link>
              )}
            </React.Fragment>
          ))}
        </nav>

        {/* Page Header */}
        <header className="mb-8">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            {pageData.seo.h1}
          </h1>
          <h2 className="text-base sm:text-lg text-gray-600">
            {pageData.seo.h2}
          </h2>
        </header>

        {/* Filters Bar */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <p className="text-gray-500">
            Showing {pageData.listings.length} of {pageData.pagination.total} properties
          </p>
          <div className="flex gap-3">
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="relevance">Most Relevant</SelectItem>
                <SelectItem value="price-low">Price: Low to High</SelectItem>
                <SelectItem value="price-high">Price: High to Low</SelectItem>
                <SelectItem value="newest">Newest First</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Listings Grid */}
        {pageData.listings.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-8">
            {pageData.listings.map((listing) => (
              <Card
                key={listing.id}
                className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                onClick={() => navigate(`/listing/${listing.id}`)}
                data-testid="property-card"
              >
                {/* Image */}
                <div className="relative aspect-video overflow-hidden bg-gray-100">
                  <LazyImage
                    src={listing.images?.[0]?.thumbnail || listing.images?.[0]?.medium || listing.images?.[0]}
                    alt={listing.title}
                    className="w-full h-full"
                  />
                  
                  {/* Badge */}
                  <div className="absolute top-2 left-2">
                    <span className={`text-xs font-bold px-2 py-1 rounded ${
                      listing.type === 'sale' ? 'bg-emerald-600 text-white' : 'bg-blue-600 text-white'
                    }`}>
                      {listing.type === 'sale' ? 'For Sale' : 'To Rent'}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <CardContent className="p-4">
                  <h3 className="font-semibold text-gray-800 text-sm sm:text-base line-clamp-1 mb-1">
                    {listing.title}
                  </h3>
                  <p className="text-lg font-bold text-emerald-600 mb-2">
                    {formatPrice(listing.price)}
                    {listing.type === 'rental' && <span className="text-xs font-normal text-gray-500">/mo</span>}
                  </p>
                  
                  {/* Specs */}
                  <div className="flex items-center gap-3 text-sm text-gray-500 mb-2">
                    {listing.specifications?.beds > 0 && (
                      <span><i className="fas fa-bed mr-1"></i>{listing.specifications.beds}</span>
                    )}
                    {listing.specifications?.baths > 0 && (
                      <span><i className="fas fa-bath mr-1"></i>{listing.specifications.baths}</span>
                    )}
                    {listing.specifications?.floorSize > 0 && (
                      <span><i className="fas fa-expand mr-1"></i>{listing.specifications.floorSize}m²</span>
                    )}
                  </div>

                  {/* Location */}
                  <p className="text-xs text-gray-400 line-clamp-1">
                    <i className="fas fa-map-marker-alt mr-1"></i>
                    {listing.propertyAddress}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-gray-50 rounded-lg mb-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
              <i className="fas fa-home text-2xl text-gray-400"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No Properties Found</h3>
            <p className="text-gray-500 mb-4">There are currently no properties matching your criteria in {pageData.suburb.name}.</p>
            <Button onClick={() => navigate('/marketplace')} variant="outline">
              Browse All Properties
            </Button>
          </div>
        )}

        {/* Pagination */}
        {pageData.pagination.totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mb-12">
            <Button
              variant="outline"
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
            >
              <i className="fas fa-chevron-left mr-2"></i>Previous
            </Button>
            
            <div className="flex items-center gap-1">
              {[...Array(Math.min(5, pageData.pagination.totalPages))].map((_, i) => {
                let pageNum;
                if (pageData.pagination.totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= pageData.pagination.totalPages - 2) {
                  pageNum = pageData.pagination.totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum ? "default" : "outline"}
                    size="sm"
                    onClick={() => handlePageChange(pageNum)}
                    className="w-10"
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            
            <Button
              variant="outline"
              disabled={currentPage === pageData.pagination.totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
            >
              Next<i className="fas fa-chevron-right ml-2"></i>
            </Button>
          </div>
        )}

        {/* Internal Links Section - SEO Boost */}
        <section className="bg-gray-50 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Explore More Properties
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {pageData.relatedPages.slice(0, 8).map((page) => (
              <Link
                key={page.url}
                to={page.url}
                className="text-sm text-emerald-600 hover:text-emerald-700 hover:underline transition-colors"
              >
                {page.text}
              </Link>
            ))}
          </div>
        </section>

        {/* FAQ Section - AEO Critical */}
        {faqData && faqData.faqItems && faqData.faqItems.length > 0 && (
          <section className="border rounded-lg p-6 mb-8" aria-labelledby="faq-heading">
            <div className="flex items-center justify-between mb-4">
              <h2 id="faq-heading" className="text-xl font-semibold text-gray-800">
                Frequently Asked Questions about {pageData.suburb.name}
              </h2>
              <button 
                onClick={() => setShowFAQ(!showFAQ)}
                className="text-emerald-600 text-sm hover:underline"
              >
                {showFAQ ? 'Hide' : 'Show'}
              </button>
            </div>
            
            {showFAQ && (
              <div className="space-y-4">
                {faqData.faqItems.map((faq, i) => (
                  <div key={i} className="border-b border-gray-100 pb-4 last:border-0 last:pb-0">
                    <h3 className="font-medium text-gray-800 mb-2">
                      <i className="fas fa-question-circle text-emerald-500 mr-2"></i>
                      {faq.question}
                    </h3>
                    <p className="text-gray-600 pl-6">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>
            )}
            
            {/* Quick Stats for Voice Search */}
            {faqData.stats && (
              <div className="mt-6 pt-4 border-t grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-emerald-600">{faqData.stats.totalListings}</p>
                  <p className="text-xs text-gray-500">Available Listings</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-800">{faqData.stats.minPrice}</p>
                  <p className="text-xs text-gray-500">Starting From</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-800">{faqData.stats.avgPrice}</p>
                  <p className="text-xs text-gray-500">Average Price</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-800">{faqData.stats.maxPrice}</p>
                  <p className="text-xs text-gray-500">Up To</p>
                </div>
              </div>
            )}
          </section>
        )}

        {/* Area Info Section */}
        <section className="prose prose-gray max-w-none mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-3">
            About {pageData.suburb.name}, {pageData.suburb.province}
          </h3>
          <p className="text-gray-600">
            Looking for property in {pageData.suburb.name}? Flippy Homes offers a wide selection of {pageData.listingConfig.propertyType?.toLowerCase() || 'properties'} {pageData.listingConfig.type === 'sale' ? 'for sale' : 'to rent'} in this sought-after {pageData.suburb.province} area. 
            Browse our verified listings with detailed photos, accurate pricing, and direct agent contact information.
          </p>
          <p className="text-gray-600">
            {pageData.suburb.name} is located in {pageData.suburb.province}, South Africa. Use our advanced search filters to find your perfect property, 
            whether you're looking for a family home, investment property, or rental accommodation.
          </p>
        </section>

        {/* CTA Section */}
        <section className="bg-emerald-50 rounded-lg p-6 text-center">
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            Can't find what you're looking for?
          </h3>
          <p className="text-gray-600 mb-4">
            Set up property alerts and be the first to know when new listings match your criteria.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate('/properties')} className="bg-emerald-600 hover:bg-emerald-700">
              <i className="fas fa-search mr-2"></i>Advanced Search
            </Button>
            <Button onClick={() => navigate('/auth')} variant="outline">
              <i className="fas fa-bell mr-2"></i>Create Alert
            </Button>
          </div>
        </section>
      </main>
    </>
  );
};

export default SuburbLandingPage;
